import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import {
  Loader2,
  Plus,
  Edit2,
  Trash2,
  FileText,
  Shield,
  Calendar,
  Download,
  AlertCircle,
  Check,
  X,
  Clock,
  AlertTriangle,
} from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { api, type PliCertificate } from '../lib/api'
import { DashboardLayout } from '../components/DashboardLayout'
import { PliUploadModal } from '../components/PliUploadModal'

export function PliCertificatesPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading, checkAuth } = useAuthStore()

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [certificates, setCertificates] = useState<PliCertificate[]>([])
  const [showModal, setShowModal] = useState(false)
  const [editingCert, setEditingCert] = useState<PliCertificate | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login', search: {} })
    }
  }, [authLoading, isAuthenticated, navigate])

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadCertificates()
    }
  }, [authLoading, isAuthenticated])

  const loadCertificates = async () => {
    try {
      setIsLoading(true)
      const data = await api.pli.list()
      setCertificates(data.certificates)
    } catch (err: any) {
      setError(err.message || 'Failed to load certificates')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this certificate?')) {
      return
    }

    setDeletingId(id)
    try {
      await api.pli.delete(id)
      setSuccess('Certificate deleted successfully')
      loadCertificates()
    } catch (err: any) {
      if (err.message?.includes('linked to bookings')) {
        setError(
          'This certificate is linked to existing bookings and cannot be deleted. It will be automatically removed after the retention period.'
        )
      } else {
        setError(err.message || 'Failed to delete certificate')
      }
    } finally {
      setDeletingId(null)
    }
  }

  const isExpired = (cert: PliCertificate) => {
    if (!cert.validUntil) return false
    return new Date(cert.validUntil) < new Date()
  }

  const isExpiringSoon = (cert: PliCertificate) => {
    if (!cert.validUntil) return false
    const validUntil = new Date(cert.validUntil)
    const thirtyDaysFromNow = new Date()
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30)
    return validUntil <= thirtyDaysFromNow && validUntil >= new Date()
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  if (authLoading || isLoading) {
    return (
      <DashboardLayout title="My Insurance" subtitle="Your public liability insurance certificates">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      </DashboardLayout>
    )
  }

  const validCerts = certificates.filter((c) => !isExpired(c))
  const expiredCerts = certificates.filter((c) => isExpired(c))

  return (
    <DashboardLayout title="My Insurance" subtitle="Your public liability insurance certificates">
      <div className="max-w-4xl">
        {/* Success/Error messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </span>
            <button onClick={() => setError('')} className="text-red-700 hover:text-red-900">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              {success}
            </span>
            <button onClick={() => setSuccess('')} className="text-green-700 hover:text-green-900">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Public Liability Insurance</h2>
            <p className="text-sm text-gray-500">
              Upload and manage your PLI certificates for room bookings that require insurance
            </p>
          </div>
          <button
            onClick={() => {
              setEditingCert(null)
              setShowModal(true)
            }}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Upload Certificate
          </button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900">Why do I need PLI?</h4>
              <p className="text-sm text-blue-700 mt-1">
                Some activities require Public Liability Insurance to protect both you and the
                venue. Upload your certificate here and you can easily attach it to bookings that
                require it.
              </p>
            </div>
          </div>
        </div>

        {/* Certificates List */}
        {certificates.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No PLI certificates uploaded</p>
            <p className="text-sm text-gray-400 mt-1">
              Upload your PLI certificate to use for bookings
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Valid Certificates */}
            {validCerts.map((cert) => (
              <div
                key={cert.id}
                className={`bg-white rounded-xl border p-4 ${
                  isExpiringSoon(cert) ? 'border-amber-300 bg-amber-50/30' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isExpiringSoon(cert)
                          ? 'bg-amber-100 text-amber-600'
                          : 'bg-green-100 text-green-600'
                      }`}
                    >
                      <Shield className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{cert.insurerName}</h3>
                      <div className="text-sm text-gray-500 space-y-0.5 mt-1">
                        {cert.policyNumber && <p>Policy: {cert.policyNumber}</p>}
                        {cert.coverageAmount && <p>Coverage: {cert.coverageAmount}</p>}
                        <p className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {cert.validFrom
                            ? formatDate(cert.validFrom)
                            : 'Start not specified'} - {formatDate(cert.validUntil)}
                        </p>
                      </div>
                      {isExpiringSoon(cert) && (
                        <div className="mt-2 flex items-center gap-1.5 text-amber-600 text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          Expires within 30 days
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <a
                      href={api.pli.getDownloadUrl(cert.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => {
                        setEditingCert(cert)
                        setShowModal(true)
                      }}
                      className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(cert.id)}
                      disabled={deletingId === cert.id}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                      title="Delete"
                    >
                      {deletingId === cert.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                {cert.notes && <p className="mt-3 text-sm text-gray-500 pl-14">{cert.notes}</p>}
                <div className="mt-3 pl-14 flex items-center gap-2 text-xs text-gray-400">
                  <FileText className="w-3.5 h-3.5" />
                  {cert.fileName}
                </div>
              </div>
            ))}

            {/* Expired Certificates */}
            {expiredCerts.length > 0 && (
              <div className="mt-8">
                <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Expired Certificates
                </h3>
                <div className="space-y-3 opacity-60">
                  {expiredCerts.map((cert) => (
                    <div
                      key={cert.id}
                      className="bg-gray-50 rounded-lg border border-gray-200 p-3 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <Shield className="w-4 h-4 text-gray-400" />
                        <div>
                          <span className="text-sm text-gray-600">{cert.insurerName}</span>
                          <span className="text-xs text-gray-400 ml-2">
                            Expired {formatDate(cert.validUntil)}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDelete(cert.id)}
                        disabled={deletingId === cert.id}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        {deletingId === cert.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Upload Modal */}
        <PliUploadModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false)
            setEditingCert(null)
          }}
          onSuccess={() => {
            setSuccess(
              editingCert ? 'Certificate updated successfully' : 'Certificate uploaded successfully'
            )
            loadCertificates()
          }}
          editingCert={editingCert}
        />
      </div>
    </DashboardLayout>
  )
}
